import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MessageSquare, CornerDownRight, Pencil, Trash2, X, Check, SmilePlus } from 'lucide-react';
import clsx from 'clsx';
import { fetchComments, createComment, updateComment, deleteComment, extractMentions, type Comment } from '@/db/comments';
import { fetchReactionsForComments, toggleReaction, type ReactionGroup } from '@/db/commentReactions';
import { useAuth } from '@/context/AuthContext';
import { useProfilesContext } from '@/context/ProfilesContext';
import { getInitials, getAvatarColor } from '@/utils/formatters';
import { notifyMention } from '@/db/notifications';
import { CommentEditor, type CommentEditorHandle } from './CommentEditor';

const QUICK_EMOJIS = ['👍', '❤️', '😄', '🎉', '🚀', '👀', '💯', '🙏'];

interface CommentsSectionProps {
  taskId: string;
}

export function CommentsSection({ taskId }: CommentsSectionProps) {
  const { user } = useAuth();
  const { resolveDisplayName } = useProfilesContext();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reactionsMap, setReactionsMap] = useState<Map<string, ReactionGroup[]>>(new Map());
  const [emojiPickerFor, setEmojiPickerFor] = useState<string | null>(null);
  const editorRef = useRef<CommentEditorHandle>(null);
  const editEditorRef = useRef<CommentEditorHandle>(null);

  const loadReactions = useCallback(async (commentIds: string[]) => {
    if (commentIds.length === 0) return;
    try {
      const map = await fetchReactionsForComments(commentIds);
      setReactionsMap(map);
    } catch {
      // silent
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await fetchComments(taskId);
      setComments(data);
      // Load reactions for all comments
      const ids = data.map(c => c.id);
      loadReactions(ids);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [taskId, loadReactions]);

  useEffect(() => { load(); }, [load]);

  // Build threaded structure
  const { roots, repliesMap } = useMemo(() => {
    const roots: Comment[] = [];
    const repliesMap = new Map<string, Comment[]>();
    for (const c of comments) {
      if (!c.parentId) {
        roots.push(c);
      } else {
        const arr = repliesMap.get(c.parentId) || [];
        arr.push(c);
        repliesMap.set(c.parentId, arr);
      }
    }
    return { roots, repliesMap };
  }, [comments]);

  const handleSubmit = async (html: string) => {
    if (!user || submitting) return;
    setSubmitting(true);
    try {
      // Extract mentions from HTML text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const textContent = tempDiv.textContent || '';
      const mentions = extractMentions(textContent);

      await createComment({
        taskId,
        userId: user.id,
        content: html,
        parentId: replyTo,
        mentions,
      });
      if (mentions.length > 0) {
        notifyMention(mentions, taskId, user.id, textContent).catch(() => {});
      }
      editorRef.current?.clear();
      setReplyTo(null);
      await load();
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setTimeout(() => editEditorRef.current?.focus(), 50);
  };

  const handleSaveEdit = async (html: string) => {
    if (!editingId || submitting) return;
    setSubmitting(true);
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const textContent = tempDiv.textContent || '';
      const mentions = extractMentions(textContent);

      await updateComment(editingId, html, mentions);
      if (mentions.length > 0 && user) {
        notifyMention(mentions, taskId, user.id, textContent).catch(() => {});
      }
      setEditingId(null);
      await load();
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteComment(id);
      await load();
    } catch {
      // silent
    }
  };

  const handleReaction = async (commentId: string, emoji: string) => {
    if (!user) return;
    setEmojiPickerFor(null);
    try {
      await toggleReaction(commentId, user.id, emoji);
      // Reload reactions for all comments
      const ids = comments.map(c => c.id);
      await loadReactions(ids);
    } catch {
      // silent
    }
  };

  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ora';
    if (mins < 60) return `${mins}m fa`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h fa`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}g fa`;
    return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  // Render HTML content (supports both rich HTML and legacy plain text)
  const renderContent = (content: string) => {
    // Check if content looks like HTML
    if (content.startsWith('<') && content.includes('</')) {
      return (
        <div
          className={clsx(
            'text-[11px] leading-relaxed mt-0.5 comment-content',
            'prose prose-invert max-w-none',
            'prose-p:text-text-secondary prose-p:my-0.5 prose-p:text-[11px] prose-p:leading-relaxed',
            'prose-a:text-accent-violet prose-a:underline',
            'prose-strong:text-text-primary prose-strong:font-semibold',
            'prose-em:text-text-secondary',
            'prose-code:text-accent-cyan prose-code:bg-surface-tertiary prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[10px]',
            'prose-pre:bg-surface-tertiary prose-pre:rounded-md prose-pre:p-2 prose-pre:text-[10px] prose-pre:my-1',
            'prose-blockquote:border-l-2 prose-blockquote:border-accent-violet prose-blockquote:pl-2 prose-blockquote:italic prose-blockquote:text-text-tertiary prose-blockquote:my-1',
            'prose-ul:text-text-secondary prose-ol:text-text-secondary prose-ul:my-0.5 prose-ol:my-0.5 prose-ul:text-[11px] prose-ol:text-[11px]',
            'prose-li:marker:text-text-quaternary prose-li:my-0',
          )}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // Legacy plain text — render with mention highlights
    const parts = content.split(/(@\[[^\]]+\]\([^)]+\))/g);
    return (
      <p className="text-[11px] text-text-secondary leading-relaxed mt-0.5 whitespace-pre-wrap break-words">
        {parts.map((part, i) => {
          const match = part.match(/@\[([^\]]+)\]\(([^)]+)\)/);
          if (match) {
            return (
              <span key={i} className="text-accent-violet font-medium">
                @{match[2]}
              </span>
            );
          }
          return part;
        })}
      </p>
    );
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isOwn = user?.id === comment.userId;
    const replies = repliesMap.get(comment.id) || [];
    const displayName = resolveDisplayName(comment.userId);

    return (
      <div key={comment.id} className={clsx('group', isReply && 'ml-6 mt-2')}>
        <div className="flex gap-2">
          {/* Avatar */}
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 mt-0.5"
            style={{ backgroundColor: getAvatarColor(comment.userId) }}
          >
            {getInitials(displayName)}
          </div>

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-text-primary">{displayName}</span>
              <span className="text-[10px] text-text-quaternary">{formatTime(comment.createdAt)}</span>
              {comment.editedAt && <span className="text-[9px] text-text-quaternary italic">(modificato)</span>}
            </div>

            {/* Content or edit form */}
            {editingId === comment.id ? (
              <div className="mt-1">
                <CommentEditor
                  ref={editEditorRef}
                  content={comment.content}
                  placeholder="Modifica commento..."
                  onSubmit={handleSaveEdit}
                  submitting={submitting}
                  autoFocus
                  submitIcon={<Check className="w-3.5 h-3.5" />}
                />
                <button
                  onClick={() => setEditingId(null)}
                  className="mt-1 flex items-center gap-1 text-[10px] text-text-quaternary hover:text-text-secondary"
                >
                  <X className="w-3 h-3" />
                  Annulla
                </button>
              </div>
            ) : (
              renderContent(comment.content)
            )}

            {/* Reactions */}
            {editingId !== comment.id && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {(reactionsMap.get(comment.id) || []).map(group => {
                  const isMine = user ? group.userIds.includes(user.id) : false;
                  return (
                    <button
                      key={group.emoji}
                      onClick={() => handleReaction(comment.id, group.emoji)}
                      title={group.userIds.map(id => resolveDisplayName(id)).join(', ')}
                      className={clsx(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] border transition-colors',
                        isMine
                          ? 'bg-accent-violet/10 border-accent-violet/30 text-accent-violet'
                          : 'bg-surface-tertiary/50 border-border-primary text-text-secondary hover:border-border-secondary'
                      )}
                    >
                      <span>{group.emoji}</span>
                      <span className="text-[10px] font-medium">{group.count}</span>
                    </button>
                  );
                })}
                {/* Add reaction button */}
                <div className="relative">
                  <button
                    onClick={() => setEmojiPickerFor(emojiPickerFor === comment.id ? null : comment.id)}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full text-text-quaternary hover:bg-surface-hover hover:text-text-secondary transition-colors opacity-0 group-hover:opacity-100"
                    title="Aggiungi reazione"
                  >
                    <SmilePlus className="w-3.5 h-3.5" />
                  </button>
                  {emojiPickerFor === comment.id && (
                    <div className="absolute left-0 bottom-full mb-1 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 p-1.5 flex gap-0.5">
                      {QUICK_EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(comment.id, emoji)}
                          className="w-7 h-7 rounded-md hover:bg-surface-hover transition-colors text-base flex items-center justify-center"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {editingId !== comment.id && (
              <div className="flex items-center gap-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {!isReply && (
                  <button
                    onClick={() => { setReplyTo(comment.id); editorRef.current?.focus(); }}
                    className="flex items-center gap-1 text-[10px] text-text-quaternary hover:text-text-secondary"
                  >
                    <CornerDownRight className="w-3 h-3" />
                    Rispondi
                  </button>
                )}
                {isOwn && (
                  <>
                    <button onClick={() => handleStartEdit(comment)} className="flex items-center gap-1 text-[10px] text-text-quaternary hover:text-text-secondary">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDelete(comment.id)} className="flex items-center gap-1 text-[10px] text-text-quaternary hover:text-accent-rose">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Replies */}
        {replies.length > 0 && (
          <div className="border-l-2 border-border-primary/50 ml-3">
            {replies.map(r => renderComment(r, true))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="px-4 py-4 border-t border-border-primary">
        <p className="text-[11px] text-text-quaternary">Caricamento commenti...</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 border-t border-border-primary">
      <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
        <MessageSquare className="w-3 h-3" />
        Commenti {comments.length > 0 && <span className="text-text-tertiary">({comments.length})</span>}
      </h3>

      {/* Comment list */}
      {roots.length > 0 && (
        <div className="space-y-3 mb-4">
          {roots.map(c => renderComment(c))}
        </div>
      )}

      {/* Reply indicator */}
      {replyTo && (
        <div className="flex items-center gap-2 mb-1.5 px-2 py-1 bg-surface-tertiary/50 rounded-lg text-[10px] text-text-tertiary">
          <CornerDownRight className="w-3 h-3" />
          <span>Rispondendo a {resolveDisplayName(comments.find(c => c.id === replyTo)?.userId || '')}</span>
          <button onClick={() => setReplyTo(null)} className="ml-auto text-text-quaternary hover:text-text-secondary">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Rich text input */}
      {user && (
        <div className="flex items-start gap-2">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 mt-1"
            style={{ backgroundColor: getAvatarColor(user.id) }}
          >
            {getInitials(resolveDisplayName(user.id))}
          </div>
          <div className="flex-1">
            <CommentEditor
              ref={editorRef}
              placeholder="Scrivi un commento... (⌘↵ per inviare)"
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          </div>
        </div>
      )}
    </div>
  );
}
